import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSite } from "@/context/SiteContext";

export const SandboxCard = () => {
  const { t } = useI18n();
  const { refresh: refreshSite } = useSite();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/sandbox").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/sandbox", { enabled });
      refreshSite();
      setInfo(data);
      toast.success(t(enabled ? "sandbox.toastOn" : "sandbox.toastOff"));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="sandbox-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
          <Boxes className="h-5 w-5 text-[var(--gold)]" /> {t("sandbox.title")}
        </h2>
        {info && (
          <Switch checked={info.enabled} disabled={busy} onCheckedChange={toggle} data-testid="sandbox-toggle" />
        )}
      </div>
      <p className="mt-2 text-sm text-neutral-400">{t("sandbox.hint")}</p>
      {info && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          <span className={`rounded-full px-2.5 py-1 font-bold uppercase ${info.enabled ? "bg-[var(--gold)] text-black" : "border border-neutral-600 text-neutral-400"}`} data-testid="sandbox-status">
            {t(info.enabled ? "sandbox.enabled" : "sandbox.disabled")}
          </span>
          <span className="text-neutral-400" data-testid="sandbox-files-count">{t("sandbox.files", { n: info.files })}</span>
          {info.enabled && (
            <Link to="/developpeurs" className="inline-flex items-center gap-1 text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="sandbox-open-link">
              <ExternalLink className="h-3.5 w-3.5" /> {t("sandbox.open")}
            </Link>
          )}
        </div>
      )}
    </section>
  );
};
